import React, { Fragment, ReactNode, useMemo } from "react";

/**
 * Web port of the Flutter `HighlightedUrlText` widget
 * (`packages/app_ui/lib/src/widgets/text/highlighted_url_text.dart`).
 *
 * Splits a plain-text body into spans. URLs matching the same regex the
 * Flutter widget uses are wrapped in an `<a>` with bold + underline
 * styling and inherit the surrounding colour so they work on any
 * background. Tapping a URL stops propagation so the surrounding card's
 * click handler doesn't also fire — it ALSO invokes `onUrlTap` so the
 * caller can run ad-click tracking before the new tab opens.
 *
 * Regex mirrors mobile verbatim:
 *   https?://\S+ | www.\S+ | \S+.(?:com|org|net|io|fm|co|uk|app)\S*
 */
const URL_PATTERN =
  /https?:\/\/\S+|www\.\S+|\S+\.(?:com|org|net|io|fm|co|uk|app)\S*/g;

const normalizeHref = (raw: string): string => {
  return raw.startsWith("http://") || raw.startsWith("https://")
    ? raw
    : `https://${raw}`;
};

export type HighlightedUrlTextProps = {
  /** The text content that may contain URLs. */
  text: string;
  /**
   * Optional tap callback invoked with the normalized URL before the
   * default `window.open` runs. Callers use this to record a click
   * event. Return is ignored.
   */
  onUrlTap?: (url: string) => void;
  /**
   * Optional max number of visually-rendered lines. When provided the
   * text is clamped via `-webkit-line-clamp`. Matches mobile's
   * `maxLines` on the underlying `Text.rich`.
   */
  maxLines?: number;
  /**
   * Optional extra className merged onto the container.
   */
  className?: string;
};

export const HighlightedUrlText: React.FC<HighlightedUrlTextProps> = ({
  text,
  onUrlTap,
  maxLines,
  className,
}) => {
  const nodes = useMemo<ReactNode[]>(() => {
    if (!text) return [];
    const out: ReactNode[] = [];
    // Reset regex lastIndex defensively — URL_PATTERN is a module-level
    // stateful regex (the `g` flag requires it) and we need it to start
    // fresh on every render.
    URL_PATTERN.lastIndex = 0;
    let lastEnd = 0;
    let match: RegExpExecArray | null;
    let idx = 0;
    while ((match = URL_PATTERN.exec(text)) !== null) {
      if (match.index > lastEnd) {
        out.push(
          <Fragment key={`t-${idx++}`}>
            {text.slice(lastEnd, match.index)}
          </Fragment>,
        );
      }
      const raw = match[0];
      const href = normalizeHref(raw);
      out.push(
        <a
          key={`a-${idx++}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="highnote-highlighted-url"
          onClick={(e) => {
            // Prevent the surrounding card's click from also firing and
            // running whatever global click/nav it does; the anchor's
            // default behaviour (open in new tab via `target="_blank"`)
            // is what we want, and `onUrlTap` runs the ad-click track.
            e.stopPropagation();
            if (onUrlTap) onUrlTap(href);
          }}
        >
          {raw}
        </a>,
      );
      lastEnd = match.index + raw.length;
      // Guard against zero-length matches infinite-looping.
      if (match.index === URL_PATTERN.lastIndex) URL_PATTERN.lastIndex++;
    }
    if (lastEnd < text.length) {
      out.push(<Fragment key={`t-${idx++}`}>{text.slice(lastEnd)}</Fragment>);
    }
    return out;
  }, [text, onUrlTap]);

  const classes = className
    ? `highnote-highlighted-url-text ${className}`
    : "highnote-highlighted-url-text";

  // When `maxLines` is set we clamp via inline style so callers don't
  // need to add per-use CSS. The `-webkit-line-clamp` approach matches
  // the single ellipsis behaviour of Flutter's `TextOverflow.ellipsis`.
  const style =
    typeof maxLines === "number" && maxLines > 0
      ? ({
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: maxLines,
          overflow: "hidden",
        } as React.CSSProperties)
      : undefined;

  return (
    <span className={classes} style={style}>
      {nodes}
    </span>
  );
};
